import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { httpStatusOf } from "@/core/http/HttpError";
import type { HttpClient } from "@/core/http/types";
import { menuService } from "../services/menuService";
import type {
  CategoryOrder, CategoryPatch, ItemPatch, MenuCategory, MenuDetail, MenuItem, MenuVersion, NewCategory, NewItem,
} from "../types";

/** Keyed by the portal client, so a second workspace in the same tab never reads the first one's menus. */
export const menuKeys = {
  all: (clientId: string) => ["menus", clientId] as const,
  list: (clientId: string) => ["menus", clientId, "list"] as const,
  menu: (clientId: string, menuId: string) => ["menus", clientId, "menu", menuId] as const,
  version: (clientId: string, menuId: string, versionId: string) => ["menus", clientId, "version", menuId, versionId] as const,
};

/** How often a running website build is read again. */
export const DEPLOY_POLL_MS = 5000;

/** A 404 is the answer for a client with menus off: asking again changes nothing. */
const retryUnlessAnswered = (failures: number, error: unknown): boolean => {
  const status = httpStatusOf(error);
  return status === null && failures < 2;
};

/**
 * The menus list, for an explicit client: the portal shell asks it before its HttpClientProvider is in the tree, and
 * shows Menu in the burger only when it answers.
 */
export function menusQuery(http: HttpClient) {
  return {
    queryKey: menuKeys.list(http.context.clientId),
    queryFn: () => menuService.list(http),
    retry: retryUnlessAnswered,
  };
}

export function useMenus() {
  const http = useHttpClient();
  return useQuery(menusQuery(http));
}

export function useMenu(menuId: string) {
  const http = useHttpClient();
  return useQuery({
    queryKey: menuKeys.menu(http.context.clientId, menuId),
    queryFn: () => menuService.menu(http, menuId),
    enabled: menuId !== "",
    retry: retryUnlessAnswered,
  });
}

/** A version's website build, read again every few seconds while it runs, and no more once it has finished. */
export function useMenuVersion(menuId: string, versionId: string | null) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const clientId = http.context.clientId;
  const query = useQuery({
    queryKey: menuKeys.version(clientId, menuId, versionId ?? ""),
    queryFn: () => menuService.version(http, menuId, versionId ?? ""),
    enabled: versionId !== null,
    refetchInterval: (q) => (q.state.data?.deployStatus === "running" ? DEPLOY_POLL_MS : false),
  });
  // A build that finished changes what the menus list says about the site.
  const status = query.data?.deployStatus;
  useEffect(() => {
    if (status !== undefined && status !== "running") void queryClient.invalidateQueries({ queryKey: menuKeys.list(clientId) });
  }, [status, clientId, queryClient]);
  return query;
}

type Detail = MenuDetail | undefined;

/** The fields an answer settles: the patched ones, and the confirmation an allergen change can end on the server. */
function settledKeys(patch: ItemPatch): (keyof MenuItem)[] {
  const keys = Object.keys(patch) as (keyof ItemPatch)[];
  return "allergens" in patch && !keys.includes("noAllergensConfirmed") ? [...keys, "noAllergensConfirmed"] : keys;
}

/**
 * The server's copy of the fields one edit sent, over the cached item. The drawer saves field by field, so an answer
 * to an earlier edit must not undo a later one still in flight.
 */
export function mergeAnswer(current: MenuItem, answer: MenuItem, patch: ItemPatch): MenuItem {
  const next: MenuItem = { ...current, updatedAt: answer.updatedAt };
  for (const key of settledKeys(patch)) {
    Object.assign(next, { [key]: answer[key] });
  }
  return next;
}

const withItems = (detail: Detail, map: (items: MenuItem[]) => MenuItem[], pending?: number): Detail =>
  detail && { ...detail, items: map(detail.items), pending: pending ?? detail.pending };

/** A category as the server answered it, new or changed, with the menu's pending count after it. */
export const withCategory = (detail: Detail, category: MenuCategory, pending?: number): Detail =>
  detail && {
    ...detail,
    categories: detail.categories.some((c) => c.id === category.id)
      ? detail.categories.map((c) => (c.id === category.id ? category : c))
      : [...detail.categories, category],
    pending: pending ?? detail.pending,
  };

/** The positions a category order sets; the categories it does not name keep theirs. */
export const withCategoryOrder = (detail: Detail, positions: { id: string; position: number }[], pending?: number): Detail => {
  const rank = new Map(positions.map((p) => [p.id, p.position]));
  return detail && {
    ...detail,
    categories: detail.categories.map((c) => {
      const position = rank.get(c.id);
      return position === undefined ? c : { ...c, position };
    }),
    pending: pending ?? detail.pending,
  };
};

/** An item put back in its category's place in the cached list. */
const inserted = (items: MenuItem[], item: MenuItem): MenuItem[] =>
  [...items.filter((i) => i.id !== item.id), item];

/**
 * Every write the editor makes. A change or a reorder may be retried, since sending it twice stores the same thing;
 * an add, a duplicate, a delete, a restore or a publish is never sent again on its own (the app's mutations retry once).
 */
export function useMenuMutations(menuId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const clientId = http.context.clientId;
  const key = menuKeys.menu(clientId, menuId);
  const update = (fn: (detail: Detail) => Detail) => queryClient.setQueryData<MenuDetail>(key, fn);
  const refreshList = () => queryClient.invalidateQueries({ queryKey: menuKeys.list(clientId) });
  const refetchMenu = (qc: QueryClient) => qc.invalidateQueries({ queryKey: key });

  const patchItem = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: ItemPatch }) => menuService.patchItem(http, menuId, itemId, patch),
    // The next field builds on this one (a second wine field, the next allergen), so the cache takes it at once.
    onMutate: async ({ itemId, patch }) => {
      await queryClient.cancelQueries({ queryKey: key });
      update((d) => withItems(d, (items) => items.map((i) => (i.id === itemId ? { ...i, ...patch } : i))));
    },
    onSuccess: (answer, { itemId, patch }) => {
      update((d) => withItems(d, (items) => items.map((i) => (i.id === itemId ? mergeAnswer(i, answer.item, patch) : i)), answer.pending));
      void refreshList();
    },
    // The server's copy, not a snapshot from before: other edits may have landed since this one left.
    onError: () => void refetchMenu(queryClient),
  });

  const createItem = useMutation({
    retry: false,
    mutationFn: (body: NewItem) => menuService.createItem(http, menuId, body),
    onSuccess: (answer) => {
      update((d) => withItems(d, (items) => inserted(items, answer.item), answer.pending));
      void refreshList();
    },
  });

  const duplicateItem = useMutation({
    retry: false,
    mutationFn: (itemId: string) => menuService.duplicateItem(http, menuId, itemId),
    // The copy is cached at once, so the drawer can move to it; the positions after it moved, so the menu is read again.
    onSuccess: (answer) => {
      update((d) => withItems(d, (items) => inserted(items, answer.item), answer.pending));
      void refetchMenu(queryClient);
      void refreshList();
    },
  });

  const deleteItem = useMutation({
    retry: false,
    mutationFn: (itemId: string) => menuService.deleteItem(http, menuId, itemId),
    onSuccess: (answer) => {
      update((d) => withItems(d, (items) => items.filter((i) => i.id !== answer.id), answer.pending));
      void refreshList();
    },
  });

  const restoreItem = useMutation({
    retry: false,
    mutationFn: (itemId: string) => menuService.restoreItem(http, menuId, itemId),
    onSuccess: (answer) => {
      update((d) => withItems(d, (items) => inserted(items, answer.item), answer.pending));
      void refreshList();
    },
  });

  const reorderItems = useMutation({
    mutationFn: ({ categoryId, itemIds }: { categoryId: string; itemIds: string[] }) =>
      menuService.reorderItems(http, menuId, categoryId, itemIds),
    onMutate: async ({ itemIds }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const rank = new Map(itemIds.map((id, index) => [id, index]));
      update((d) => withItems(d, (items) => items.map((i) => {
        const position = rank.get(i.id);
        return position === undefined ? i : { ...i, position };
      })));
    },
    onSuccess: (answer) => {
      update((d) => d && { ...d, pending: answer.pending });
      void refreshList();
    },
    onError: () => void refetchMenu(queryClient),
  });

  const createCategory = useMutation({
    retry: false,
    mutationFn: (body: NewCategory) => menuService.createCategory(http, menuId, body),
    onSuccess: (answer) => {
      update((d) => withCategory(d, answer.category, answer.pending));
      void refreshList();
    },
  });

  const patchCategory = useMutation({
    mutationFn: ({ categoryId, patch }: { categoryId: string; patch: CategoryPatch }) =>
      menuService.patchCategory(http, menuId, categoryId, patch),
    // The header shows the new name or the dimming at once, as an item's field does.
    onMutate: async ({ categoryId, patch }) => {
      await queryClient.cancelQueries({ queryKey: key });
      update((d) => {
        const category = d?.categories.find((c) => c.id === categoryId);
        return category ? withCategory(d, { ...category, ...patch }) : d;
      });
    },
    // Only the fields this edit sent, so an earlier answer never undoes a later edit still in flight.
    onSuccess: (answer, { categoryId, patch }) => {
      update((d) => {
        const category = d?.categories.find((c) => c.id === categoryId);
        if (!category) return d;
        const settled: CategoryPatch = {};
        if (patch.name !== undefined) settled.name = answer.category.name;
        if (patch.hidden !== undefined) settled.hidden = answer.category.hidden;
        return withCategory(d, { ...category, ...settled }, answer.pending);
      });
      void refreshList();
    },
    onError: () => void refetchMenu(queryClient),
  });

  const reorderCategories = useMutation({
    mutationFn: (order: CategoryOrder) => menuService.reorderCategories(http, menuId, order),
    onMutate: async ({ categoryIds }) => {
      await queryClient.cancelQueries({ queryKey: key });
      update((d) => withCategoryOrder(d, categoryIds.map((id, position) => ({ id, position }))));
    },
    onSuccess: (answer) => {
      update((d) => withCategoryOrder(d, answer.categories, answer.pending));
      void refreshList();
    },
    onError: () => void refetchMenu(queryClient),
  });

  const publish = useMutation({
    retry: false,
    mutationFn: () => menuService.publish(http, menuId),
    onSuccess: (version: MenuVersion) => {
      queryClient.setQueryData(menuKeys.version(clientId, menuId, version.id), version);
      update((d) => d && { ...d, pending: 0, latestVersion: version, menu: { ...d.menu, publishedVersionId: version.id } });
      void refetchMenu(queryClient);
      void refreshList();
    },
  });

  return {
    patchItem, createItem, duplicateItem, deleteItem, restoreItem, reorderItems,
    createCategory, patchCategory, reorderCategories, publish,
  };
}

export type MenuMutations = ReturnType<typeof useMenuMutations>;
